/** Shared shape for Escalation Matrix UI (list + form). */

const LEVEL_KEYS = ['L1', 'L2', 'L3'];

export const ESCALATION_MATRIX_CSV_HEADERS = [
  'Department',
  'TAT',
  'Level',
  'Role',
  'Name',
  'Mobile',
  'Email',
  'Stores'
];

export function emptyUser() {
  return {
    id: '',
    name: '',
    mobile: '',
    email: '',
    storeCodes: ''
  };
}

export function emptyLevel() {
  return { role: '', users: [emptyUser()] };
}

export function emptyMatrixRow(idx = 0) {
  return {
    _rowKey: `row_${idx}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    id: '',
    department: '',
    tat: '',
    L1: emptyLevel(),
    L2: emptyLevel(),
    L3: emptyLevel()
  };
}

/** Any stores input (array, semicolon string, single id) → API stores array */
export function toStoreIdsArray(value) {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(s => String(s).trim()).filter(Boolean);
  }
  const str = String(value).trim();
  if (!str) {
    return [];
  }
  return str
    .split(';')
    .map(s => s.trim())
    .filter(Boolean);
}

/** API stores array or semicolon string → form storeCodes string */
export function storesToStoreCodes(stores) {
  return toStoreIdsArray(stores).join(';');
}

/** semicolon-separated store codes → API stores array (strings) */
export function storeCodesToStores(storeCodes) {
  return toStoreIdsArray(storeCodes);
}

function levelToForm(lvl) {
  if (!lvl) {
    return emptyLevel();
  }
  const users = Array.isArray(lvl.users) && lvl.users.length
    ? lvl.users.map(u => normalizeFormUser({
        id: u._id || u.id || '',
        name: u.name,
        mobile: u.mobile,
        email: u.email,
        stores: u.stores,
        storeIds: u.storeIds,
        storeCodes: u.storeCodes
      }))
    : [emptyUser()];
  return {
    role: lvl.role || '',
    users
  };
}

export function rowToForm(row, idx) {
  return {
    _rowKey: `row_${idx}_${row.id || ''}`,
    id: row.id,
    department: row.department || '',
    tat: row.tat != null ? row.tat : '',
    L1: levelToForm(row.L1),
    L2: levelToForm(row.L2),
    L3: levelToForm(row.L3)
  };
}

function normalizeUserField(value) {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}

function normalizeFormUser(user = {}) {
  const storeCodes = storesToStoreCodes(user.storeCodes || user.stores || user.storeIds);
  return {
    id: user.id || user._id || '',
    name: normalizeUserField(user.name),
    mobile: normalizeUserField(user.mobile),
    email: normalizeUserField(user.email),
    storeCodes
  };
}

function userHasAnyData(user) {
  const normalized = normalizeFormUser(user);
  return !!(normalized.name || normalized.mobile || normalized.email || normalized.storeCodes);
}

function userToPayload(user) {
  const normalized = normalizeFormUser(user);
  const stores = toStoreIdsArray(normalized.storeCodes);
  const out = {
    name: normalized.name,
    mobile: normalized.mobile,
    email: normalized.email,
    stores
  };
  if (user.id) {
    out._id = user.id;
  }
  return out;
}

function levelToPayload(level) {
  const users = Array.isArray(level?.users) ? level.users : [];
  return {
    role: (level && level.role) || '',
    users: users.filter(userHasAnyData).map(userToPayload)
  };
}

export function buildEscalationPayload(form) {
  const { key, isActive, matrix } = form;
  return {
    key,
    isActive,
    matrix: matrix.map(row => ({
      ...(row.id ? { _id: row.id } : {}),
      department: row.department,
      tat: Number(row.tat) || 0,
      L1: levelToPayload(row.L1),
      L2: levelToPayload(row.L2),
      L3: levelToPayload(row.L3)
    }))
  };
}

export function userFieldKey(rowKey, level, uIdx, field) {
  return `${rowKey}-${level}-${uIdx}-${field}`;
}

/** Returns invalid field keys for name, mobile, email on non-empty user rows. */
export function validateEscalationForm(form) {
  const invalidKeys = new Set();
  const issues = [];
  if (!form || !Array.isArray(form.matrix)) {
    return { valid: false, invalidKeys, issues, message: 'Matrix is empty.' };
  }

  form.matrix.forEach(row => {
    const department = (row.department || 'Untitled department').trim();
    LEVEL_KEYS.forEach(level => {
      const users = row[level]?.users || [];
      users.forEach((user, uIdx) => {
        if (!userHasAnyData(user)) {
          return;
        }
        const normalized = normalizeFormUser(user);
        const missing = [];
        if (!normalized.name) {
          invalidKeys.add(userFieldKey(row._rowKey, level, uIdx, 'name'));
          missing.push('name');
        }
        if (!normalized.mobile) {
          invalidKeys.add(userFieldKey(row._rowKey, level, uIdx, 'mobile'));
          missing.push('mobile');
        }
        if (!normalized.email) {
          invalidKeys.add(userFieldKey(row._rowKey, level, uIdx, 'email'));
          missing.push('email');
        }
        if (missing.length) {
          issues.push(`${department} / ${level} / User ${uIdx + 1}: missing ${missing.join(', ')}`);
        }
      });
    });
  });

  const message = issues.length
    ? `Name, mobile, and email are required for each assignee.\n${issues.slice(0, 5).join('\n')}${
        issues.length > 5 ? `\n…and ${issues.length - 5} more.` : ''
      }`
    : '';

  return {
    valid: invalidKeys.size === 0,
    invalidKeys,
    issues,
    message
  };
}

function parseStoresCell(storesStr) {
  if (storesStr == null || !String(storesStr).trim()) {
    return [];
  }
  return toStoreIdsArray(String(storesStr).replace(/\|/g, ';'));
}

function normalizeCsvRow(row) {
  const normalized = {};
  Object.keys(row || {}).forEach(key => {
    const cleanKey = key.trim().replace(/^\ufeff/, '').toLowerCase();
    normalized[cleanKey] = row[key];
  });
  return normalized;
}

function csvMobileValue(row) {
  const mobile =
    row.mobile ??
    row.phone ??
    row['mobile number'] ??
    row['phone number'] ??
    row.mobileno ??
    row.phonenumber;
  return normalizeUserField(mobile);
}

export function assertBulkCsvHeaders(csvRows = []) {
  if (!csvRows.length) {
    throw new Error('CSV file is empty.');
  }
  const keys = Object.keys(csvRows[0] || {}).map(key =>
    key.trim().replace(/^\ufeff/, '').toLowerCase()
  );
  const hasMobile = keys.some(key =>
    ['mobile', 'phone', 'mobile number', 'phone number', 'mobileno', 'phonenumber'].includes(key)
  );
  if (!hasMobile) {
    throw new Error(
      `CSV is missing a Mobile column. Expected headers: ${ESCALATION_MATRIX_CSV_HEADERS.join(', ')}`
    );
  }
}

export function normalizeUploadedMatrix(matrix = []) {
  return matrix.map((row, idx) => {
    const normalizedRow = {
      ...row,
      _rowKey: row._rowKey || `row_${idx}_${Date.now()}`,
      L1: normalizeUploadedLevel(row.L1),
      L2: normalizeUploadedLevel(row.L2),
      L3: normalizeUploadedLevel(row.L3)
    };
    return normalizedRow;
  });
}

function normalizeUploadedLevel(level) {
  if (!level) {
    return emptyLevel();
  }
  const users = Array.isArray(level.users) && level.users.length
    ? level.users.map(normalizeFormUser)
    : [emptyUser()];
  return {
    role: (level.role || '').trim(),
    users
  };
}

export function buildBulkFormFromUpload(form, { matrix, key, isActive }) {
  const uploadedMatrix = normalizeUploadedMatrix(matrix);
  return {
    key: (key || form?.key || 'hr_management').trim(),
    isActive: isActive != null ? !!isActive : !!form?.isActive,
    matrix: uploadedMatrix.length ? uploadedMatrix : [emptyMatrixRow(0)]
  };
}

/** Flat CSV rows (one row per user per level) → form matrix rows */
export function matrixRowsFromBulkCsv(csvRows = []) {
  assertBulkCsvHeaders(csvRows);
  const deptMap = new Map();

  csvRows.forEach((rawRow, lineIdx) => {
    const row = normalizeCsvRow(rawRow);
    const department = normalizeUserField(row.department);
    const tat = row.tat;
    const level = normalizeUserField(row.level).toUpperCase();
    const role = normalizeUserField(row.role);
    const name = normalizeUserField(row.name);
    const mobile = csvMobileValue(row);
    const email = normalizeUserField(row.email);
    const storeCodes = storesToStoreCodes(parseStoresCell(row.stores));

    if (!department) {
      throw new Error(`Row ${lineIdx + 2}: Department is required.`);
    }
    if (!LEVEL_KEYS.includes(level)) {
      throw new Error(`Row ${lineIdx + 2}: Level must be L1, L2, or L3.`);
    }

    const deptKey = department.toLowerCase();
    if (!deptMap.has(deptKey)) {
      deptMap.set(deptKey, {
        department,
        tat: tat !== '' && tat != null ? Number(tat) : '',
        levels: {
          L1: { role: '', users: [] },
          L2: { role: '', users: [] },
          L3: { role: '', users: [] }
        }
      });
    }

    const entry = deptMap.get(deptKey);
    if (tat !== '' && tat != null && !Number.isNaN(Number(tat))) {
      entry.tat = Number(tat);
    }

    const lvl = entry.levels[level];
    if (role) {
      lvl.role = role;
    }
    if (name || mobile || email || storeCodes) {
      lvl.users.push(normalizeFormUser({ id: '', name, mobile, email, storeCodes }));
    }
  });

  return normalizeUploadedMatrix(
    Array.from(deptMap.values()).map((entry, idx) => {
      const formRow = emptyMatrixRow(idx);
      formRow.department = entry.department;
      formRow.tat = entry.tat;
      LEVEL_KEYS.forEach(level => {
        const src = entry.levels[level];
        formRow[level] = {
          role: src.role,
          users: src.users.length ? src.users : [emptyUser()]
        };
      });
      return formRow;
    })
  );
}

/** JSON document (QMS escalation matrix shape) → form matrix rows + optional meta */
export function parseBulkEscalationJson(json) {
  const data = typeof json === 'string' ? JSON.parse(json) : json;
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid JSON: expected an object.');
  }

  const matrix = Array.isArray(data.matrix) ? data.matrix : Array.isArray(data) ? data : null;
  if (!matrix) {
    throw new Error('Invalid format: expected a "matrix" array.');
  }
  if (!matrix.length) {
    throw new Error('Matrix is empty.');
  }

  return {
    key: data.key || '',
    isActive: data.isActive != null ? !!data.isActive : null,
    matrix: normalizeUploadedMatrix(matrix.map((row, idx) => rowToForm(row, idx)))
  };
}

export function applyBulkMatrixToForm(form, { matrix, key, isActive }) {
  return buildBulkFormFromUpload(form, { matrix, key, isActive });
}

function escapeCsvCell(value) {
  const str = value == null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Nested matrix (API or form shape) → flat CSV matching bulk-upload sample format */
export function matrixToBulkCsv(matrix = []) {
  const csvRows = [];

  matrix.forEach(row => {
    const department = (row.department || '').trim();
    const tat = row.tat !== '' && row.tat != null ? row.tat : '';

    LEVEL_KEYS.forEach(level => {
      const lvl = row[level];
      if (!lvl) {
        return;
      }
      const role = (lvl.role || '').trim();
      const users = Array.isArray(lvl.users) && lvl.users.length ? lvl.users : [];

      users.forEach(user => {
        const normalized = normalizeFormUser(user);
        if (!userHasAnyData(user) && !role) {
          return;
        }
        csvRows.push([
          department,
          tat,
          level,
          role,
          normalized.name,
          normalized.mobile,
          normalized.email,
          normalized.storeCodes
        ]);
      });
    });
  });

  const lines = [
    ESCALATION_MATRIX_CSV_HEADERS.join(','),
    ...csvRows.map(row => row.map(escapeCsvCell).join(','))
  ];
  return `${lines.join('\n')}\n`;
}

/** Sample CSV content derived from the QMS escalation matrix format */
export function buildEscalationMatrixSampleCsv() {
  const sampleRows = [
    ['Payroll', 3, 'L1', 'PAYROLL_L1', 'Subarna Ghosh', '9876543210', 'karan@attabot.io', '23;234;235'],
    ['Payroll', 3, 'L1', 'PAYROLL_L1', 'Subarna Ghosh', '9876543210', 'karan@attabot.io', ''],
    ['Payroll', 3, 'L2', 'PAYROLL_L2', 'Loknarayan Panna', '9876543211', 'karan@attabot.io', ''],
    ['Payroll', 3, 'L3', 'CHRO', 'CHRO', '9876543212', 'karan@attabot.io', ''],
    ['Comp & Benefit', 3, 'L1', 'C&B_L1', 'Arpita Bhowmick', '9876543213', 'karan@attabot.io', ''],
    ['Comp & Benefit', 3, 'L2', 'C&B_L2', 'Loknarayan Panna', '9876543211', 'karan@attabot.io', ''],
    ['Comp & Benefit', 3, 'L3', 'CHRO', 'CHRO', '9876543212', 'karan@attabot.io', ''],
    ['In-store', 7, 'L1', 'InStore_L1', 'Piyali Bhattacharya', '9876543214', 'karan@attabot.io', ''],
    ['In-store', 7, 'L2', 'C&B_L2', 'RHR', '9876543215', 'karan@attabot.io', ''],
    ['In-store', 7, 'L3', 'CHRO', 'CHRO', '9876543212', 'karan@attabot.io', '']
  ];

  const lines = [
    ESCALATION_MATRIX_CSV_HEADERS.join(','),
    ...sampleRows.map(row => row.map(escapeCsvCell).join(','))
  ];
  return `${lines.join('\n')}\n`;
}

/** Sample JSON content matching the QMS escalation matrix API document */
export function buildEscalationMatrixSampleJson() {
  return JSON.stringify(
    {
      key: 'hr_management',
      isActive: true,
      matrix: [
        {
          department: 'Payroll',
          tat: 3,
          L1: {
            role: 'PAYROLL_L1',
            users: [
              { name: 'Subarna Ghosh', mobile: '9876543210', email: 'karan@attabot.io', stores: ['23', '234', '235'] },
              { name: 'Subarna Ghosh', mobile: '9876543210', email: 'karan@attabot.io', stores: [] }
            ]
          },
          L2: {
            role: 'PAYROLL_L2',
            users: [{ name: 'Loknarayan Panna', mobile: '9876543211', email: 'karan@attabot.io', stores: [] }]
          },
          L3: {
            role: 'CHRO',
            users: [{ name: 'CHRO', mobile: '9876543212', email: 'karan@attabot.io', stores: [] }]
          }
        },
        {
          department: 'Comp & Benefit',
          tat: 3,
          L1: {
            role: 'C&B_L1',
            users: [{ name: 'Arpita Bhowmick', mobile: '9876543213', email: 'karan@attabot.io', stores: [] }]
          },
          L2: {
            role: 'C&B_L2',
            users: [{ name: 'Loknarayan Panna', mobile: '9876543211', email: 'karan@attabot.io', stores: [] }]
          },
          L3: {
            role: 'CHRO',
            users: [{ name: 'CHRO', mobile: '9876543212', email: 'karan@attabot.io', stores: [] }]
          }
        },
        {
          department: 'In-store',
          tat: 7,
          L1: {
            role: 'InStore_L1',
            users: [{ name: 'Piyali Bhattacharya', mobile: '9876543214', email: 'karan@attabot.io', stores: [] }]
          },
          L2: {
            role: 'C&B_L2',
            users: [{ name: 'RHR', mobile: '9876543215', email: 'karan@attabot.io', stores: [] }]
          },
          L3: {
            role: 'CHRO',
            users: [{ name: 'CHRO', mobile: '9876543212', email: 'karan@attabot.io', stores: [] }]
          }
        }
      ]
    },
    null,
    2
  );
}

export function downloadTextFile({ content, filename, mimeType = 'text/plain;charset=utf-8' }) {
  const anchor = document.createElement('a');
  anchor.href = `data:${mimeType},${encodeURIComponent(content)}`;
  anchor.target = '_blank';
  anchor.download = filename;
  anchor.click();
}
